import { isDefined } from '@utils/isDefined';

export abstract class LazyUpdater<UD extends object> {
    protected updateTimeout = 5; // Задержка между запросами в секундах
    private updateData?: UD;
    private requestTimeout?: NodeJS.Timeout;
    private isOnUpdate = false;
    private requestInFlight?: Promise<void>;

    public update(data: UD): void {
        if (isDefined(this.updateData)) {
            this.updateUpdateRequest(data);
        } else {
            this.createNewUpdateRequest(data);
        }
    }

    public async updateImmediately(data?: UD): Promise<void> {
        if (isDefined(data)) {
            this.update(data);
        }
        this.clearRequestTimeout();
        await this.executeRequest();
    }

    protected mergeData(currentData: UD, newData: UD): UD {
        return { ...currentData, ...newData };
    }

    protected abstract sendUpdateRequest(requestData: UD): Promise<void>;

    private async executeRequest(): Promise<void> {
        if (!!this.updateData) {
            this.isOnUpdate = true;
            const updateData = { ...this.updateData };
            this.updateData = undefined;
            this.isOnUpdate = false;
            this.requestInFlight = this.sendAfterPreviousRequest(updateData);
        }
        await this.requestInFlight;
    }

    private async sendAfterPreviousRequest(updateData: UD): Promise<void> {
        await this.requestInFlight?.catch(() => undefined);
        await this.sendUpdateRequest(updateData);
    }

    private clearRequestTimeout(): void {
        if (isDefined(this.requestTimeout)) {
            clearTimeout(this.requestTimeout);
            this.requestTimeout = undefined;
        }
    }

    private createNewUpdateRequest(data: UD): void {
        this.updateData = data;
        this.requestTimeout = setTimeout(() => this.executeRequest(), this.updateTimeout * 1000);
    }

    private updateUpdateRequest(data: UD): void {
        if (!this.isOnUpdate) {
            this.isOnUpdate = true;
            this.updateData = this.mergeData(this.updateData ?? ({} as UD), data);
            this.isOnUpdate = false;
        } else {
            setTimeout(() => {
                this.update(data);
            }, 100);
        }
    }
}
